import React, {useEffect, useState} from 'react';
import {InputFieldComponent} from "../input-field/input-field.component";
import styled, {css} from "styled-components";
import {PopperDropDownModel} from "./popper-drop-down.model";
import {useServices} from "../../hooks/use-services.hook";
import {IDropDownController} from "./drop-down-controller.interface";
import {NullableString} from "../../types/nullable-types";
import {observer} from "mobx-react";
import {BOX_BORDER_RADIUS_PX} from "../css-global-constants";
import {DropDownIndicatorComponent} from "./components/indicator/drop-down-indicator.component";

const DropDownListWrapperBox = styled.div<{isOpen: boolean; isMobileView: boolean}>`
    overflow: hidden;
    display: ${props => props.isOpen ? 'block' : 'none'};
    background-color: ${props => props.theme.colors.primaryContrast};
    color: ${props => props.theme.colors.lightContrast};
    border: 1px solid ${props => props.theme.colors.medium};
    border-radius: ${BOX_BORDER_RADIUS_PX};
    border-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    ${
        props => props.isMobileView 
                    ? css`
                      border: none;
                    `
                    : ''
    }
`

const DropDownContentBox = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
`




function isClickInsideElement(event: MouseEvent, element: Element): boolean {
    const rect = element.getBoundingClientRect();
    return (rect.left <= event.x && event.x <= rect.right)
            && (rect.top <= event.y && event.y <= rect.bottom);
}

interface CustomDropDownComponentProps {
    renderInput: (controller: IDropDownController) => React.ReactElement;
    renderList: (controller: IDropDownController) => React.ReactElement;
    renderDropDownIndicator?: (controller: IDropDownController) => React.ReactElement | null;
    onOpen: () => void;
    onClose: () => void;
    label: string;
    errorMessage: NullableString;
    icon?: React.ReactElement;
}

export const CustomDropDownComponent: React.FC<CustomDropDownComponentProps> = observer((props) => {
    const services = useServices();
    const inputFieldRef = React.createRef<HTMLDivElement>();
    const [popper, setPopper] = useState<PopperDropDownModel | null>(null)
    const dropDownListRef = React.createRef<HTMLDivElement>();
    const isOpen = Boolean(popper);

    useEffect(() => {
        if(isOpen) {
            document.addEventListener('click', onDocumentClickHandler);
            return () => {
                document.removeEventListener('click', onDocumentClickHandler);
            }
        }

        return () => {};
    });



    const openDropDown = () => {
        if(isOpen) {
            return;
        }

        if(inputFieldRef.current && dropDownListRef.current) {
            setPopper(new PopperDropDownModel(inputFieldRef.current, dropDownListRef.current, services));
        }

        props.onOpen();
    }

    const closeDropDown = () => {
        if(popper) {
            popper.dispose();
            setPopper(null);
            props.onClose();
        }
    }

    const onClickHandler = () => {
        if(isOpen) {
            closeDropDown();
        } else {
            openDropDown();
        }
    }



    const onDocumentClickHandler = (event: MouseEvent) => {
        if(dropDownListRef.current && inputFieldRef.current) {
            if(!isClickInsideElement(event, dropDownListRef.current)
                && !isClickInsideElement(event, inputFieldRef.current)) {
                //it means the user clicked outside
                closeDropDown();
            }
        }
    }

    const controller: IDropDownController = {
        closeDropDown: closeDropDown,
        openDropDown: openDropDown,
        isOpen: () => isOpen
    };

    const renderDropDownIndicatorContent = () => {
        if(props.renderDropDownIndicator) {
            const indicatorContent = props.renderDropDownIndicator(controller);
            if(indicatorContent) {
                return indicatorContent;
            }
        }

        return (
            <DropDownIndicatorComponent isOpen={isOpen} dropDownController={controller}/>
        );
    }




    return (
        <>
            <InputFieldComponent onClick={onClickHandler}
                                 label={props.label}
                                 errorMessage={props.errorMessage}
                                 icon={props.icon}
                                 renderDropDownIndicator={renderDropDownIndicatorContent}
                                 ref={inputFieldRef}>

                <DropDownContentBox>
                    {props.renderInput(controller)}
                </DropDownContentBox>
            </InputFieldComponent>
            <DropDownListWrapperBox ref={dropDownListRef}
                                    isOpen={isOpen}
                                    isMobileView={services.application.isInMobileView}
                                    className={"tst-drop-down-wrapper"}>
                {props.renderList(controller)}
            </DropDownListWrapperBox>
        </>

    );
})
